'use client'
import { useEffect, useState } from 'react'
import { fetchPropertyReviews, fetchSimilarProperties } from '@/queries'

const fetchFirstPagePropertyReviews = (propertyId) =>
  fetchPropertyReviews(propertyId, 1)

const useDeferredResource = ({
  id,
  initialData,
  initialLoading,
  fetchResource,
}) => {
  const [data, setData] = useState(initialData)
  const [isLoading, setIsLoading] = useState(initialLoading)

  useEffect(() => {
    setData(initialData)
    setIsLoading(initialLoading)
  }, [initialData, initialLoading])

  useEffect(() => {
    if (!id || initialData || !initialLoading) {
      return
    }

    let isCancelled = false

    const loadResource = async () => {
      try {
        const response = await fetchResource(id)

        if (!isCancelled) {
          setData(response?.data ?? null)
        }
      } catch {
        if (!isCancelled) {
          setData(null)
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    loadResource()

    return () => {
      isCancelled = true
    }
  }, [id, initialData, initialLoading, fetchResource])

  return { data, isLoading }
}

export const useDeferredPropertyExtras = ({
  id,
  reviewsData,
  loadingReviews,
  similarData,
  loadingSimilar,
}) => {
  const deferredReviews = useDeferredResource({
    id,
    initialData: reviewsData,
    initialLoading: loadingReviews,
    fetchResource: fetchFirstPagePropertyReviews,
  })

  const deferredSimilarProperties = useDeferredResource({
    id,
    initialData: similarData,
    initialLoading: loadingSimilar,
    fetchResource: fetchSimilarProperties,
  })

  return {
    reviewsData: deferredReviews.data,
    isReviewsLoading: deferredReviews.isLoading,
    similarPropertiesData: deferredSimilarProperties.data,
    isSimilarLoading: deferredSimilarProperties.isLoading,
  }
}